import {
  getRotatedBox,
  normalizeRotation,
  parseSvgBox,
  transformPointBetweenRotations,
} from './pinCalloutLayout';

const roundViewBoxNumber = (value) => Number(Number(value).toFixed(2));

export const isValidSvgViewBox = (viewBox) => {
  const minX = Number(viewBox?.minX);
  const minY = Number(viewBox?.minY);
  const width = Number(viewBox?.width);
  const height = Number(viewBox?.height);

  return Number.isFinite(minX)
    && Number.isFinite(minY)
    && Number.isFinite(width)
    && width > 0
    && Number.isFinite(height)
    && height > 0;
};

export const normalizeSvgViewBox = (viewBox) => {
  if (!isValidSvgViewBox(viewBox)) {
    return null;
  }

  return {
    minX: roundViewBoxNumber(viewBox.minX),
    minY: roundViewBoxNumber(viewBox.minY),
    width: roundViewBoxNumber(viewBox.width),
    height: roundViewBoxNumber(viewBox.height),
  };
};

export const areSvgViewBoxesEqual = (leftViewBox, rightViewBox) => {
  const normalizedLeftViewBox = normalizeSvgViewBox(leftViewBox);
  const normalizedRightViewBox = normalizeSvgViewBox(rightViewBox);

  if (!normalizedLeftViewBox || !normalizedRightViewBox) {
    return normalizedLeftViewBox === normalizedRightViewBox;
  }

  return normalizedLeftViewBox.minX === normalizedRightViewBox.minX
    && normalizedLeftViewBox.minY === normalizedRightViewBox.minY
    && normalizedLeftViewBox.width === normalizedRightViewBox.width
    && normalizedLeftViewBox.height === normalizedRightViewBox.height;
};

export const parseSvgViewBoxFromMarkup = (svgMarkup) => {
  const box = parseSvgBox(svgMarkup);
  return normalizeSvgViewBox(box);
};

export const applySvgViewBoxToMarkup = (svgMarkup, viewBox) => {
  const normalizedViewBox = normalizeSvgViewBox(viewBox);
  const trimmedMarkup = String(svgMarkup || '').trim();

  if (!trimmedMarkup || !normalizedViewBox || typeof window === 'undefined') {
    return trimmedMarkup;
  }

  try {
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(trimmedMarkup, 'image/svg+xml');
    const parseError = parsedDocument.querySelector('parsererror');
    const svgElement = parsedDocument.documentElement;

    if (parseError || !svgElement || svgElement.nodeName.toLowerCase() !== 'svg') {
      return trimmedMarkup;
    }

    svgElement.setAttribute(
      'viewBox',
      `${normalizedViewBox.minX} ${normalizedViewBox.minY} ${normalizedViewBox.width} ${normalizedViewBox.height}`
    );
    svgElement.setAttribute('width', String(normalizedViewBox.width));
    svgElement.setAttribute('height', String(normalizedViewBox.height));

    return new XMLSerializer().serializeToString(svgElement);
  } catch (error) {
    return trimmedMarkup;
  }
};

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';
const PRESERVE_TAGS = new Set(['defs', 'style', 'title', 'desc', 'metadata']);

const createSvgElement = (doc, tagName, attributes = {}) => {
  const element = doc.createElementNS(SVG_NAMESPACE, tagName);
  Object.entries(attributes).forEach(([name, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      element.setAttribute(name, String(value));
    }
  });
  return element;
};

const getSvgRotationTransform = (box, rotation) => {
  const normalizedRotation = normalizeRotation(rotation);

  switch (normalizedRotation) {
    case 90:
      return `translate(${box.minX + box.height + box.minY} ${box.minY - box.minX}) rotate(90)`;
    case 180:
      return `translate(${(2 * box.minX) + box.width} ${(2 * box.minY) + box.height}) rotate(180)`;
    case 270:
      return `translate(${box.minX - box.minY} ${box.minY + box.width + box.minX}) rotate(270)`;
    default:
      return '';
  }
};

export const applySvgArtworkRotationToMarkup = (svgMarkup, rotation) => {
  const trimmedMarkup = String(svgMarkup || '').trim();
  const normalizedRotation = normalizeRotation(rotation);

  if (!trimmedMarkup || normalizedRotation === 0 || typeof window === 'undefined') {
    return trimmedMarkup;
  }

  try {
    const parser = new DOMParser();
    const parsedDocument = parser.parseFromString(trimmedMarkup, 'image/svg+xml');
    const parseError = parsedDocument.querySelector('parsererror');
    const svgElement = parsedDocument.documentElement;

    if (parseError || !svgElement || svgElement.nodeName.toLowerCase() !== 'svg') {
      return trimmedMarkup;
    }

    const sourceBox = normalizeSvgViewBox(parseSvgBox(trimmedMarkup));
    if (!sourceBox) {
      return trimmedMarkup;
    }

    const rotationWrapper = createSvgElement(parsedDocument, 'g', {
      transform: getSvgRotationTransform(sourceBox, normalizedRotation),
      'data-micropinouts-public-rotation': '1',
    });

    [...svgElement.childNodes].forEach((childNode) => {
      if (
        childNode.nodeType === Node.ELEMENT_NODE
        && PRESERVE_TAGS.has(childNode.nodeName.toLowerCase())
      ) {
        return;
      }

      rotationWrapper.appendChild(childNode);
    });

    svgElement.appendChild(rotationWrapper);

    const rotatedBox = normalizeSvgViewBox(getRotatedBox(sourceBox, normalizedRotation));
    if (!rotatedBox) {
      return trimmedMarkup;
    }

    svgElement.setAttribute(
      'viewBox',
      `${rotatedBox.minX} ${rotatedBox.minY} ${rotatedBox.width} ${rotatedBox.height}`
    );
    svgElement.setAttribute('width', String(rotatedBox.width));
    svgElement.setAttribute('height', String(rotatedBox.height));

    return new XMLSerializer().serializeToString(svgElement);
  } catch (error) {
    return trimmedMarkup;
  }
};

export const rotateSvgViewBox = (viewBox, svgMarkup, fromRotation, toRotation) => {
  const normalizedViewBox = normalizeSvgViewBox(viewBox);
  if (!normalizedViewBox) {
    return null;
  }

  const normalizedFromRotation = normalizeRotation(fromRotation);
  const normalizedToRotation = normalizeRotation(toRotation);

  if (normalizedFromRotation === normalizedToRotation) {
    return normalizedViewBox;
  }

  const sourceBox = parseSvgBox(svgMarkup);
  const corners = [
    { x: normalizedViewBox.minX, y: normalizedViewBox.minY },
    { x: normalizedViewBox.minX + normalizedViewBox.width, y: normalizedViewBox.minY },
    { x: normalizedViewBox.minX, y: normalizedViewBox.minY + normalizedViewBox.height },
    { x: normalizedViewBox.minX + normalizedViewBox.width, y: normalizedViewBox.minY + normalizedViewBox.height },
  ].map((point) => transformPointBetweenRotations(point, sourceBox, normalizedFromRotation, normalizedToRotation));

  const xValues = corners.map((point) => point.x);
  const yValues = corners.map((point) => point.y);

  return normalizeSvgViewBox({
    minX: Math.min(...xValues),
    minY: Math.min(...yValues),
    width: Math.max(...xValues) - Math.min(...xValues),
    height: Math.max(...yValues) - Math.min(...yValues),
  });
};
